import { type ResponsiveTableColumn } from '@optimization/ssi-common';
import { type VehicleListColumnKey, type VehicleRowItem } from '..';
import { type VehicleCycledEnergyRecord } from '../../services/battery-monitoring-api/types/battery-monitoring-api';
import { warningColumn } from '../utils/warning-column';
import { NotAvailable } from '../../NotAvailable';
import { lifetimeDailyTooltip } from '../../operational-tooltips';
import { renderStateOfHealthCell, stateOfHealthColumn } from '../utils/state-of-health-column';
import { renderVehicleTypeCell } from '../utils/vehicle-type-cell';
import { OperationalMetric } from './cells';

interface Period {
  readonly type?: string | null;
  readonly from?: string | null;
  readonly to?: string | null;
}

const formatDate = (date?: string | null): string => date?.split('T')[0] ?? 'unknown';

const periodTooltip = (description: string, period: Period): string => {
  let label = 'Period unavailable';
  if (period.type === 'UsageLifetime') label = 'Usage lifetime';
  return `${description}. ${label}: ${formatDate(period.from)} to ${formatDate(period.to)}. May include previous owners' operation.`;
};

const dailyTooltip = (description: string, vehicle: VehicleCycledEnergyRecord): string =>
  lifetimeDailyTooltip(description, vehicle.operationalFromDate, vehicle.operationalToDate);

const trafficValue = (vehicle: VehicleCycledEnergyRecord): string | null => {
  const speed = vehicle.averageSpeedKph;
  const traffic = vehicle.traffic;
  if ((traffic === null || traffic === undefined) && (speed === null || speed === undefined)) return null;
  const speedText = speed === null || speed === undefined ? 'N/A' : speed.toFixed(0);
  return `${traffic ?? 'N/A'} · ${speedText}`;
};

export const getOperationalColumns = (): ResponsiveTableColumn<VehicleRowItem, VehicleListColumnKey>[] => [
  {
    columnTitle: 'Chassis',
    columnKey: 'chassisNumber',
    sortable: true,
    render: ({ vehicle }) => vehicle.chassisNumber,
  },
  { columnTitle: 'Type', columnKey: 'vehicleType', sortable: true, render: renderVehicleTypeCell },
  {
    columnTitle: 'Topography',
    columnKey: 'topography',
    sortable: true,
    render: ({ vehicle: v }) => (
      <OperationalMetric
        id={`op-${v.chassisNumber}-topography`}
        value={v.topography}
        attention={v.topography?.toLowerCase() === 'very hilly'}
        tooltip={periodTooltip('Topography category', {
          type: v.topographyPeriodType,
          from: v.topographyFromDate,
          to: v.topographyToDate,
        })}
      />
    ),
  },
  {
    columnTitle: 'Avg. Temp.',
    columnKey: 'temperatureMedianC',
    sortable: true,
    render: ({ vehicle: v }) => (
      <OperationalMetric
        id={`op-${v.chassisNumber}-temperature`}
        value={v.temperatureMedianC}
        unit="°C"
        mutedUnit={false}
        attention={typeof v.temperatureMedianC === 'number' && (v.temperatureMedianC < 5 || v.temperatureMedianC > 25)}
        tooltip={periodTooltip('Median ambient temperature', {
          type: v.temperaturePeriodType,
          from: v.temperatureFromDate,
          to: v.temperatureToDate,
        })}
      />
    ),
  },
  {
    columnTitle: 'Traffic · Avg. Speed',
    textAlign: 'right',
    columnKey: 'traffic',
    sortable: true,
    render: ({ vehicle: v }) => (
      <OperationalMetric
        id={`op-${v.chassisNumber}-traffic`}
        value={trafficValue(v)}
        displayValue={
          <>
            {v.traffic ?? <NotAvailable />} ·{' '}
            {typeof v.averageSpeedKph === 'number' ? v.averageSpeedKph.toFixed(0) : <NotAvailable />}
          </>
        }
        unit={v.averageSpeedKph === null || v.averageSpeedKph === undefined ? undefined : 'km/h'}
        rightAligned
        attention={v.traffic === 'Dense' || v.traffic === 'Start/Stop' || v.traffic === 'Start/Stop**'}
        tooltip={`${periodTooltip('Traffic category', { type: v.trafficPeriodType, from: v.trafficFromDate, to: v.trafficToDate })} ${periodTooltip('Average speed', { type: v.speedPeriodType, from: v.speedFromDate, to: v.speedToDate })}`}
      />
    ),
  },
  {
    columnTitle: 'GTW (75th)',
    textAlign: 'right',
    columnKey: 'gtwQ3Tonnes',
    sortable: true,
    render: ({ vehicle: v }) => (
      <OperationalMetric
        id={`op-${v.chassisNumber}-gtw`}
        value={v.gtwQ3Tonnes}
        unit="tons"
        rightAligned
        digits={1}
        attention={typeof v.gtwQ3Tonnes === 'number' && v.gtwQ3Tonnes > 44}
        tooltip={dailyTooltip(
          '75th percentile (Q3) of daily GTW estimates from transport work and distance, shown in tonnes',
          v,
        )}
      />
    ),
  },
  {
    columnTitle: 'Daily Dist.',
    textAlign: 'right',
    columnKey: 'dailyDistanceQ3Km',
    sortable: true,
    render: ({ vehicle: v }) => (
      <OperationalMetric
        id={`op-${v.chassisNumber}-distance`}
        value={v.dailyDistanceQ3Km}
        unit="km"
        mutedUnit={false}
        rightAligned
        attention={typeof v.dailyDistanceQ3Km === 'number' && v.dailyDistanceQ3Km > 499}
        tooltip={dailyTooltip('75th percentile (Q3) of daily distance on days above 1 km', v)}
      />
    ),
  },
  {
    columnTitle: 'E.C.',
    textAlign: 'right',
    columnKey: 'totalEcPerKm',
    sortable: true,
    render: ({ vehicle: v }) => (
      <OperationalMetric
        id={`op-${v.chassisNumber}-ec`}
        value={v.propulsion === 'PHEV' || v.totalEcPerKm === 0 ? null : v.totalEcPerKm}
        unavailable={v.propulsion === 'PHEV'}
        unit="kWh/km"
        rightAligned
        digits={1}
        tooltip="Electricity consumption per kilometer calculated on request from currently available lifetime operational history through yesterday in Europe/Berlin, including previous owners. Zero-electricity days are excluded from both electricity and distance totals."
      />
    ),
  },
  { ...stateOfHealthColumn, render: (row) => renderStateOfHealthCell(row, false) },
  warningColumn,
];
